if (!customElements.get('localization-form')) {
  const normalizeCountrySearch = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase(document.documentElement.lang || 'en');

  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.menuDrawer = document.querySelector('.menu-drawer');
        this.addedScrollLockClass = null;
        this.deliveryViewportMql = window.matchMedia('(max-width: 989px)');
        this.deliveryPortal = null;
        this.deliveryPageState = null;
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[data-country-search-results]'),
          overlay: this.querySelector('.country-selector__overlay'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', () => this.hidePanel({ restoreFocus: true }));
        }
        if (this.elements.overlay) {
          this.elements.overlay.addEventListener('click', () => this.hidePanel({ restoreFocus: true }));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));

        if (this.hasAttribute('data-delivery-country-localization')) {
          this.deliveryViewportMql.addEventListener?.('change', (event) => {
            if (!event.matches && this.elements.button.getAttribute('aria-expanded') === 'true') {
              this.hidePanel({ restoreFocus: true });
            }
          });
        }
      }

      hidePanel({ restoreFocus = false } = {}) {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        this.releasePageState();
        if (restoreFocus) (this.returnFocusTo || this.elements.button).focus?.();
      }

      releasePageState() {
        this.releaseDeliveryViewport();
        if (this.addedScrollLockClass) {
          document.body.classList.remove(this.addedScrollLockClass);
          this.addedScrollLockClass = null;
        }
        this.menuDrawer?.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      isDeliveryViewportSelector() {
        return this.hasAttribute('data-delivery-country-localization') && this.deliveryViewportMql.matches;
      }

      mountDeliveryViewport() {
        if (this.deliveryPortal) return;
        const wrapper = this.closest('.splus-delivery-country-localization');
        if (!wrapper || !wrapper.parentNode) return;
        const placeholder = document.createComment('delivery-country-localization');
        wrapper.parentNode.insertBefore(placeholder, wrapper);
        document.body.appendChild(wrapper);
        this.deliveryPortal = { placeholder, wrapper };
      }

      captureStyles(element, properties) {
        return properties.map((property) => ({
          property,
          value: element.style.getPropertyValue(property),
          priority: element.style.getPropertyPriority(property),
        }));
      }

      restoreStyles(element, styles) {
        styles.forEach(({ property, value, priority }) => {
          if (value) element.style.setProperty(property, value, priority);
          else element.style.removeProperty(property);
        });
      }

      lockDeliveryViewport() {
        if (this.deliveryPageState) return;
        const htmlProperties = ['overflow', 'overscroll-behavior'];
        const bodyProperties = ['position', 'top', 'left', 'width', 'overflow', 'overscroll-behavior'];
        const scrollX = window.scrollX || 0;
        const scrollY = window.scrollY || 0;
        this.deliveryPageState = {
          scrollX,
          scrollY,
          htmlStyles: this.captureStyles(document.documentElement, htmlProperties),
          bodyStyles: this.captureStyles(document.body, bodyProperties),
        };
        document.documentElement.style.setProperty('overflow', 'hidden');
        document.documentElement.style.setProperty('overscroll-behavior', 'none');
        document.body.style.setProperty('position', 'fixed');
        document.body.style.setProperty('top', `${-scrollY}px`);
        document.body.style.setProperty('left', `${-scrollX}px`);
        document.body.style.setProperty('width', '100%');
        document.body.style.setProperty('overflow', 'hidden');
        document.body.style.setProperty('overscroll-behavior', 'none');
      }

      releaseDeliveryViewport() {
        if (this.deliveryPortal) {
          const { placeholder, wrapper } = this.deliveryPortal;
          if (placeholder.parentNode) placeholder.parentNode.insertBefore(wrapper, placeholder);
          placeholder.remove();
          this.deliveryPortal = null;
        }
        if (!this.deliveryPageState) return;
        const { scrollX, scrollY, htmlStyles, bodyStyles } = this.deliveryPageState;
        this.restoreStyles(document.documentElement, htmlStyles);
        this.restoreStyles(document.body, bodyStyles);
        this.deliveryPageState = null;
        window.scrollTo(scrollX, scrollY);
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel({ restoreFocus: true });
            event.stopPropagation();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        const opening = this.elements.panel.hasAttribute('hidden');
        const deliveryViewportSelector = opening && this.isDeliveryViewportSelector();
        if (deliveryViewportSelector) this.mountDeliveryViewport();
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', opening.toString());
        if (!opening) {
          this.releasePageState();
          (this.returnFocusTo || this.elements.button).focus?.();
          return;
        }
        if (deliveryViewportSelector) {
          this.lockDeliveryViewport();
          this.elements.closeButton?.focus();
          return;
        }
        let scrollLockClass = null;
        if (window.matchMedia('(max-width: 749px)').matches) {
          scrollLockClass = 'overflow-hidden-mobile';
        } else if (window.matchMedia('(min-width: 750px) and (max-width: 989px)').matches) {
          scrollLockClass = 'overflow-hidden-tablet';
        }
        if (scrollLockClass && !document.body.classList.contains(scrollLockClass)) {
          document.body.classList.add(scrollLockClass);
          this.addedScrollLockClass = scrollLockClass;
        }
        if (this.elements.search && (this.mql.matches || this.returnFocusTo)) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        this.menuDrawer?.classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      filterCountries() {
        const searchValue = normalizeCountrySearch(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('.countries a');
        let visibleCountries = 0;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = normalizeCountrySearch(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
